import React, { useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { BellIcon, XCircleIcon } from '@heroicons/react/outline';
import { Button } from 'components/Button';
import { Modal } from 'components/Modal';
import { ChooseReturnKlaviyoTemplateModal } from 'components/ChooseReturnKlaviyoTemplateModal';
import { EmailPreviewFrame } from 'components/EmailPreviewFrame';
import { apiErrorHandler, NotificationTypes, SizeEnum } from 'utils';
import { getEventTypeMeta } from 'utils/klaviyoEventTypeMeta';
import { returnEmailTemplateTypes } from 'containers/NestedContainers/Tracking/data/templates-data';
import {
  GET_RETURN_KLAVIYO_FLOW_SETUP_STATUS,
  GET_RETURN_KLAVIYO_FLOWS_STATUS,
  GET_RETURN_KLAVIYO_TEMPLATE_PREVIEWS,
  GET_RETURN_KLAVIYO_TEMPLATE_STYLES,
} from 'gql/queries';
import { SETUP_RETURN_KLAVIYO_FLOWS } from 'gql/mutations';

const ALREADY_IN_PROGRESS_ERROR =
  'Flow setup is already in progress for this store';
const TOTAL_FLOW_COUNT = 6;
const SETUP_STATUS_POLL_INTERVAL_MS = 2500;
const TEMPLATE_PREVIEW_POLL_INTERVAL_MS = 1500;
const DEFAULT_TEMPLATE_STYLE = 'klaviyo';

export const SEND_COUNT_WINDOWS = [
  { id: 30, label: 'Last 30 days' },
  { id: 90, label: 'Last 90 days' },
] as const;

type FlowSetupFailure = { flowId: string; error: string };
type FlowSetupSummary = {
  created: string[];
  failed: FlowSetupFailure[];
  archiveWarning?: string | null;
  error?: string | null;
};

type ReturnEmailTemplateSectionProps = {
  storeId?: string;
  connectionTemplateStyle?: string | null;
  showNotification: (type: string, message: string) => void;
  sendCountWindow?: number;
  onChangeSendCountWindow?: (window: number) => void;
};

export const ReturnEmailTemplateSection = ({
  storeId,
  connectionTemplateStyle,
  showNotification,
  sendCountWindow,
  onChangeSendCountWindow,
}: ReturnEmailTemplateSectionProps) => {
  const apolloClient = useApolloClient();
  const isSettingUpFlowsRef = useRef(false);
  const [setupSummary, setSetupSummary] = useState<FlowSetupSummary | null>(
    null,
  );
  const [isPollingSetupStatus, setIsPollingSetupStatus] = useState(false);
  // Captured once, at the moment a run starts, rather than derived live from
  // allFlowsAlreadyConfigured — that value can change mid-run as flows finish importing, which
  // would otherwise flip this label partway through the very run it's describing.
  const [runningActionLabel, setRunningActionLabel] = useState<
    'Importing flows…' | 'Updating templates…'
  >('Importing flows…');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [pendingTemplateStyle, setPendingTemplateStyle] = useState<
    string | null
  >(null);

  const currentTemplateStyle =
    pendingTemplateStyle || connectionTemplateStyle || DEFAULT_TEMPLATE_STYLE;

  // Rendering all previews for a style can take a while on a cold cache — the backend
  // kicks off a background render and returns PENDING immediately, so this has to poll like
  // returnKlaviyoFlowSetupStatus rather than treat the first response as final.
  const [previewsReady, setPreviewsReady] = useState(false);
  useEffect(() => {
    setPreviewsReady(false);
  }, [currentTemplateStyle]);

  const { data: previewsData, loading: previewsLoading } = useQuery(
    GET_RETURN_KLAVIYO_TEMPLATE_PREVIEWS,
    {
      variables: { templateStyle: currentTemplateStyle },
      pollInterval: previewsReady ? 0 : TEMPLATE_PREVIEW_POLL_INTERVAL_MS,
      notifyOnNetworkStatusChange: true,
    },
  );
  useEffect(() => {
    if (previewsData?.returnKlaviyoTemplatePreviews?.status === 'DONE') {
      setPreviewsReady(true);
    }
  }, [previewsData]);
  const templatePreviews =
    previewsData?.returnKlaviyoTemplatePreviews?.previews ?? [];
  const representativePreview = templatePreviews.find(
    (preview: any) => preview.eventType === 'RETURN_REVIEWED',
  );

  const { data: stylesData } = useQuery(GET_RETURN_KLAVIYO_TEMPLATE_STYLES);
  const currentStyleName = stylesData?.returnKlaviyoTemplateStyles?.find(
    (style: any) => style.id === currentTemplateStyle,
  )?.name;

  const { data: flowsStatusData } = useQuery(GET_RETURN_KLAVIYO_FLOWS_STATUS, {
    // Scoped to currentTemplateStyle (not just whatever's persisted) so a merchant who picks a
    // style that was already fully imported earlier sees that immediately, instead of the count
    // for whichever style happens to still be active until they click "Import flows".
    variables: { storeId, templateStyle: currentTemplateStyle },
    skip: !storeId,
    fetchPolicy: 'cache-and-network',
    // Flows are created one at a time on the backend — while a setup run is in progress, poll
    // at the same cadence so the count ticks up live instead of only updating once at the end.
    pollInterval: isPollingSetupStatus ? SETUP_STATUS_POLL_INTERVAL_MS : 0,
  });
  const configuredFlowCount =
    flowsStatusData?.returnKlaviyoFlowsStatus?.length || 0;
  // "All flows exist" alone isn't "nothing to do" — a merchant can pick a different style in
  // the modal that was already fully imported from an earlier run but isn't the *active* one
  // (its flows are still sitting in Draft). Clicking is still what actually flips it live and
  // drafts the previous style, so the button only truly has nothing to do once currentTemplateStyle
  // is both fully imported AND already the one persisted as active.
  const allFlowsAlreadyConfigured = configuredFlowCount >= TOTAL_FLOW_COUNT;
  const isAlreadyActiveStyle =
    currentTemplateStyle ===
    (connectionTemplateStyle ?? DEFAULT_TEMPLATE_STYLE);
  const nothingToApply = allFlowsAlreadyConfigured && isAlreadyActiveStyle;
  const hasImportIssue = Boolean(
    setupSummary?.error || setupSummary?.failed.length,
  );
  // Visible any time there's something to import (so a merchant doesn't have to open the Edit
  // modal just to kick off the initial import), and stays visible as a retry affordance if the
  // last run left something broken — hidden only once every flow for the active style exists.
  const showImportButton = !allFlowsAlreadyConfigured || hasImportIssue;

  const [setupReturnKlaviyoFlows, { loading: setupLoading }] = useMutation(
    SETUP_RETURN_KLAVIYO_FLOWS,
  );

  // Resumes the polling UI if a setup run is already active server-side when this component
  // mounts (e.g. after a page refresh mid-setup) — only reacts to PENDING, never DONE, so it
  // can't re-surface a stale success/failure toast for a run that already finished before this
  // mount.
  useQuery(GET_RETURN_KLAVIYO_FLOW_SETUP_STATUS, {
    variables: { storeId },
    skip: !storeId,
    fetchPolicy: 'network-only',
    onCompleted(data) {
      if (data?.returnKlaviyoFlowSetupStatus?.status === 'PENDING') {
        setIsPollingSetupStatus(true);
      }
    },
  });

  useQuery(GET_RETURN_KLAVIYO_FLOW_SETUP_STATUS, {
    variables: { storeId },
    skip: !storeId || !isPollingSetupStatus,
    pollInterval: SETUP_STATUS_POLL_INTERVAL_MS,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const result = data?.returnKlaviyoFlowSetupStatus;
      if (!result || result.status !== 'DONE') return;

      setIsPollingSetupStatus(false);
      apolloClient.refetchQueries({ include: ['returnKlaviyoFlowsStatus'] });
      const createdCount = result.created?.length || 0;
      const failed: FlowSetupFailure[] = result.failed || [];
      setSetupSummary({
        created: result.created || [],
        failed,
        archiveWarning: result.archiveWarning,
        error: result.error,
      });

      if (result.error) {
        showNotification(NotificationTypes.error, result.error);
      } else if (failed.length) {
        showNotification(
          NotificationTypes.pending,
          `${createdCount} of ${TOTAL_FLOW_COUNT} flows set up successfully. ${failed.length} failed.`,
        );
      } else if (result.archiveWarning) {
        // Deliberately not a plain success toast — a leftover live flow from the previous
        // template style means duplicate emails until the merchant manually cleans it up in
        // Klaviyo, so this needs to read as a warning even though the new style set up fine.
        showNotification(NotificationTypes.pending, result.archiveWarning);
      } else {
        showNotification(
          NotificationTypes.success,
          'Your template has been updated and set live!',
        );
      }
    },
    onError(error) {
      setIsPollingSetupStatus(false);
      const newError = apiErrorHandler(error);
      showNotification(
        NotificationTypes.error,
        newError?.message ||
          'Something went wrong checking flow setup status, please try again.',
      );
    },
  });

  const handleSelectTemplateStyle = (style: string) => {
    setPendingTemplateStyle(style);
    handleSetupFlows(style);
  };

  const handleSetupFlows = async (styleOverride?: string) => {
    if (
      !storeId ||
      isSettingUpFlowsRef.current ||
      setupLoading ||
      isPollingSetupStatus
    )
      return;
    const styleToApply = styleOverride ?? currentTemplateStyle;
    isSettingUpFlowsRef.current = true;
    setSetupSummary(null);
    setRunningActionLabel(
      allFlowsAlreadyConfigured ? 'Updating templates…' : 'Importing flows…',
    );

    try {
      await setupReturnKlaviyoFlows({
        variables: { storeId, templateStyle: styleToApply },
      });
      setIsPollingSetupStatus(true);
    } catch (error: any) {
      const newError = apiErrorHandler(error);
      if (newError?.message?.includes(ALREADY_IN_PROGRESS_ERROR)) {
        showNotification(
          NotificationTypes.pending,
          'Setup is already running, please wait.',
        );
        setIsPollingSetupStatus(true);
      } else {
        showNotification(
          NotificationTypes.error,
          newError?.message ||
            'Something went wrong setting up flows, please try again.',
        );
      }
    } finally {
      isSettingUpFlowsRef.current = false;
    }
  };

  return (
    <div className="mb-6">
      <div className="flex flex-wrap items-end gap-3">
        <p className="text-lg text-[#64748b]">
          Email template:{' '}
          <span className="text-2xl font-semibold text-[#0b1c30]">
            {currentStyleName}
          </span>
        </p>
        <button
          type="button"
          onClick={() => setShowPreviewModal(true)}
          disabled={
            previewsLoading || !representativePreview || isPollingSetupStatus
          }
          className="rounded-md border border-[#d8dfeb] bg-white px-2.5 py-1 text-xs font-semibold text-[#334155] shadow-sm transition hover:bg-[#eff4ff] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Preview
        </button>
        <button
          type="button"
          onClick={() => setShowTemplateModal(true)}
          disabled={isPollingSetupStatus}
          className="rounded-md border border-[#bfdbfe] bg-white px-2.5 py-1 text-xs font-semibold text-[#2563eb] shadow-sm transition hover:bg-[#eff4ff] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Edit
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          {showImportButton ? (
            <Button
              onClick={() => handleSetupFlows()}
              size={SizeEnum.md}
              color="none"
              variant="outlined"
              className="rounded-lg border-[#d8dfeb] bg-white text-[#334155] hover:bg-[#eff4ff] disabled:cursor-not-allowed disabled:hover:bg-white"
              isLoading={setupLoading || isPollingSetupStatus}
              disabled={setupLoading || isPollingSetupStatus || nothingToApply}
            >
              {setupLoading || isPollingSetupStatus
                ? runningActionLabel
                : allFlowsAlreadyConfigured
                ? 'Update templates'
                : 'Import flows'}
            </Button>
          ) : null}
          <div className="flex flex-wrap items-center gap-2 text-sm text-[#64748b]">
            <BellIcon className="h-4 w-4 flex-shrink-0 text-[#737686]" />
            <span>
              {configuredFlowCount}/{TOTAL_FLOW_COUNT} flows configured
            </span>
            <a
              href="https://www.klaviyo.com/flows"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[#2563eb] hover:text-[#1d4ed8]"
            >
              View flows
            </a>
          </div>
        </div>
        {configuredFlowCount > 0 &&
        sendCountWindow !== undefined &&
        onChangeSendCountWindow ? (
          <div className="flex items-center gap-1.5">
            {SEND_COUNT_WINDOWS.map((window) => (
              <button
                key={window.id}
                type="button"
                onClick={() => onChangeSendCountWindow(window.id)}
                className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  sendCountWindow === window.id
                    ? 'bg-[#262626] text-white'
                    : 'bg-[#eef1f6] text-[#64748b] hover:bg-[#e2e7f0]'
                }`}
              >
                {window.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {isPollingSetupStatus || setupSummary ? (
        <div className="mt-3">
          {isPollingSetupStatus ? (
            <p className="text-xs leading-5 text-[#64748b]">
              This can take a minute. You can leave this page — setup will
              continue in the background.
            </p>
          ) : setupSummary ? (
            <div className="space-y-3">
              {setupSummary.error ? (
                <div className="rounded-lg border border-[#fecaca] bg-[#fef2f2] px-3 py-2.5 text-xs leading-5 text-[#b91c1c]">
                  <p className="font-semibold">Flow setup failed.</p>
                  <p className="mt-1">{setupSummary.error}</p>
                </div>
              ) : (
                <>
                  <div
                    className={`rounded-lg border px-3 py-2.5 text-xs leading-5 ${
                      setupSummary.failed.length
                        ? 'border-[#fed7aa] bg-[#fff8ed] text-[#9a3412]'
                        : 'border-[#bbf0cc] bg-[#eafbf0] text-[#15803d]'
                    }`}
                  >
                    <p className="font-semibold">
                      {setupSummary.failed.length
                        ? `${setupSummary.created.length} of ${TOTAL_FLOW_COUNT} flows set up successfully.`
                        : 'Your template has been updated and set live!'}
                    </p>
                    {setupSummary.failed.length ? (
                      <>
                        <p className="mt-1">
                          {setupSummary.failed.length} flow
                          {setupSummary.failed.length > 1 ? 's' : ''} failed.
                          Click the button above again to retry — already
                          created flows are skipped automatically.
                        </p>
                        <ul className="mt-2 space-y-1">
                          {setupSummary.failed.map((failure) => (
                            <li
                              key={failure.flowId}
                              className="flex items-start gap-2"
                            >
                              <XCircleIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#c2410c]" />
                              <span>{failure.error}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : null}
                  </div>
                  <p className="text-xs leading-5 text-[#64748b]">
                    These flows are now live and sending. Manage individual
                    flows any time from the table below, or open them directly
                    in Klaviyo.
                  </p>
                </>
              )}
              {setupSummary.archiveWarning ? (
                <div className="rounded-lg border border-[#fed7aa] bg-[#fff8ed] px-3 py-2.5 text-xs leading-5 text-[#9a3412]">
                  <p className="font-semibold">
                    Your previous template style may still be active.
                  </p>
                  <p className="mt-1">{setupSummary.archiveWarning}</p>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <ChooseReturnKlaviyoTemplateModal
        show={showTemplateModal}
        setShowModal={setShowTemplateModal}
        currentTemplateStyle={currentTemplateStyle}
        onSelectTemplateStyle={handleSelectTemplateStyle}
      />

      <Modal
        openModal={setShowPreviewModal}
        open={showPreviewModal}
        containerClass="w-full max-w-5xl !p-0 overflow-hidden"
      >
        <div className="px-6 pb-6 pt-1">
          <h2 className="mb-1 text-lg font-semibold text-[#0b1c30]">
            Preview all {TOTAL_FLOW_COUNT} emails
          </h2>
          <p className="mb-4 text-sm leading-6 text-[#64748b]">
            Every notification email for the current template.
          </p>
          {!previewsReady ? (
            <p className="mb-3 flex items-center gap-2 text-xs text-[#64748b]">
              <span
                style={{
                  borderColor: 'rgba(37,99,235, 0.25)',
                  borderTopColor: '#2564eb',
                }}
                className="inline-block h-3 w-3 animate-spin rounded-full border-2"
              />
              Loading previews… {templatePreviews.length}/{TOTAL_FLOW_COUNT}
            </p>
          ) : null}
          <div className="grid max-h-[65vh] grid-cols-1 gap-4 overflow-y-auto sm:grid-cols-3">
            {templatePreviews.map((preview: any) => {
              const meta = getEventTypeMeta(
                preview.eventType,
                returnEmailTemplateTypes,
              );
              const Icon = meta.icon;
              return (
                <div
                  key={preview.eventType}
                  className="overflow-hidden rounded-lg border border-[#d8dfeb] bg-[#f8fbff]"
                >
                  <div className="flex items-center gap-2 border-b border-[#d8dfeb] bg-white px-3 py-2">
                    {Icon ? (
                      <Icon className="h-4 w-4 text-[#334155]" />
                    ) : meta.imgSrc ? (
                      <img src={meta.imgSrc} alt="" className="h-4 w-4" />
                    ) : null}
                    <span className="truncate text-xs font-semibold text-[#0b1c30]">
                      {meta.label}
                    </span>
                  </div>
                  <EmailPreviewFrame
                    html={preview.html}
                    title={preview.flowName}
                    className="w-full"
                  />
                </div>
              );
            })}
          </div>
        </div>
      </Modal>
    </div>
  );
};
